import { Sub as RadixSub } from '@radix-ui/react-dropdown-menu';
import {
  useEffect,
  useMemo,
  type ReactNode,
  Children,
  isValidElement,
  cloneElement,
} from 'react';
import { isElement } from 'react-is';

import { type WithChildren } from '@dynatrace/strato-components/core';
import { _mergeProps } from '@dynatrace/strato-components/core';

import { MenuSubContext } from './contexts/MenuSubContext.js';
import { SubContent, type MenuSubContentProps } from './SubContent.js';
import { SubTrigger } from './SubTrigger.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';

/**
 * @public
 */
export interface MenuSubProps extends WithChildren {
  /**
   * The open state of the dropdown menu when it is initially rendered. Use when
   * you do not need to control its open state.
   */
  defaultOpen?: boolean;
  /**
   * The controlled open state of the dropdown menu. Must be used in conjunction
   * with onOpenChange.
   */
  open?: boolean;
  /**
   * Event handler called when the open state of the dropdown menu changes.
   * @param isOpen - true if the menu has been opened
   */
  onOpenChange?: (isOpen: boolean) => void;
}

type SubSlots = {
  trigger: ReactNode | null;
  content: ReactNode[];
  freeform: ReactNode[];
};

/** Iterates over all top-level SubContent nodes and registers tab key handler. */
function registerTabHandlers(
  children: ReactNode,
  handleTabKeyPressed: () => void,
): ReactNode {
  return Children.map(children, (child: ReactNode) => {
    return isValidElement<MenuSubContentProps>(child) &&
      child.type === SubContent
      ? cloneElement(
          child,
          _mergeProps(child.props, {
            _onTabKeyPressed: handleTabKeyPressed,
          }),
          child.props.children,
        )
      : child;
  });
}

/** Iterates over child nodes to extract specific component slots. */
function getSubSlots(children: ReactNode) {
  const slots: SubSlots = {
    trigger: null,
    content: [],
    freeform: [],
  };

  Children.forEach(children, (child) => {
    if (isElement(child)) {
      switch (child.type) {
        case SubTrigger:
          slots.trigger = child;
          break;
        case SubContent:
          slots.content.push(child);
          break;
        default:
          slots.freeform.push(child);
      }
    } else {
      slots.freeform.push(child);
    }
  });

  return slots;
}

/**
 * @public
 */
export const Sub = (props: MenuSubProps) => {
  const {
    defaultOpen: defaultValue,
    open: value,
    onOpenChange: onChange,
    children,
    ...remainingProps
  } = props;
  const [open, updateOpen] = useControllableState({
    defaultValue,
    value,
    onChange,
  });
  const slots = useMemo(
    () => getSubSlots(registerTabHandlers(children, () => updateOpen(false))),
    [children, updateOpen],
  );

  // Closes the Menu on window blur to fix inherent issue with Radix UI keeping submenus open.
  // https://github.com/radix-ui/primitives/issues/1441
  const onWindowBlur = () => {
    updateOpen(false);
  };

  useEffect(() => {
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('blur', onWindowBlur);
    };
  });

  return (
    <MenuSubContext.Provider
      value={{
        isOpen: open,
        isDefaultOpen: defaultValue ?? false,
        setIsOpen: updateOpen,
      }}
    >
      <RadixSub
        open={open}
        onOpenChange={(isOpen) => updateOpen(isOpen)}
        {...remainingProps}
      >
        {slots.trigger !== null && slots.trigger}
        {open && slots.content}
        {slots.freeform}
      </RadixSub>
    </MenuSubContext.Provider>
  );
};

(Sub as typeof Sub & { displayName: string }).displayName = 'Menu.Sub';
